import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Users, Database, ShieldAlert, Table2, Palette, SlidersHorizontal } from "lucide-react";
import ImpostazioniStudio from "./ImpostazioniStudio";
import Operatori from "./Operatori";
import BackupRestore from "./BackupRestore";
import ConfigTablesSettings from "./components/settings/ConfigTablesSettings";
import DesignSystemPage from "./components/DesignSystemPage";
import SessionPreferencesSettings from "./components/settings/SessionPreferencesSettings";

/**
 * Local error boundary: isolates a single sub-tab so a React reconciliation
 * crash (e.g. the recurring "removeChild on Node" React 19 issue triggered
 * by Radix portals + DOM injections from browser extensions/password
 * managers on Windows) does NOT take down the whole Impostazioni page.
 */
class SubTabErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[Impostazioni:${this.props.label}] crash isolato:`, error, info);
  }
  reset = () => {
    this.setState({ hasError: false, error: null });
  };
  render() {
    if (this.state.hasError) {
      return (
        <div className="border border-amber-200 bg-amber-50 rounded-md p-4 text-sm text-amber-900 space-y-2" data-testid={`subtab-error-${this.props.testid || "default"}`}>
          <p className="font-semibold">
            Impossibile caricare la sezione "{this.props.label}".
          </p>
          <p className="text-xs">
            {this.state.error?.message || "Errore imprevisto durante il rendering."}
          </p>
          <button
            type="button"
            onClick={this.reset}
            className="px-3 py-1.5 bg-amber-600 text-white rounded-md text-xs hover:bg-amber-700"
          >
            Riprova
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Role-based access control for the "Operatori" sub-section.
 *
 * Business rule (26/02/2026):
 *  - Only `amministratore` and `responsabile` may view, create, edit,
 *    or delete operators/users from Impostazioni → Operatori.
 *  - `operatore_studio` and `operatore_agenzia` keep full operational
 *    access elsewhere, but cannot see the Operatori sub-tab nor reach
 *    its content (defense-in-depth: menu visibility + sub-tab guard +
 *    backend verify_admin on mutation endpoints).
 */
const OPERATORI_ALLOWED_ROLES = new Set(["amministratore", "responsabile"]);
const canManageOperators = (role) => OPERATORI_ALLOWED_ROLES.has(role);

function UnauthorizedOperatori() {
  return (
    <div
      className="border border-red-200 bg-red-50 rounded-md p-6 text-sm text-red-900 space-y-3"
      data-testid="operatori-unauthorized"
    >
      <div className="flex items-center gap-2 font-semibold text-base">
        <ShieldAlert className="h-5 w-5 text-red-600" />
        Accesso non autorizzato
      </div>
      <p>
        La gestione degli operatori è riservata ad <strong>Amministratori</strong> e{" "}
        <strong>Responsabili</strong>. Il tuo ruolo non consente di visualizzare o
        modificare questa sezione.
      </p>
      <p className="text-xs text-red-700">
        Se ritieni che si tratti di un errore, contatta un amministratore del
        sistema.
      </p>
    </div>
  );
}

/**
 * Combined Settings Component
 * Contains "Dati Azienda", "Operatori", and "Backup & Restore" sections
 *
 * IMPORTANT (React 19 stability):
 *  - Sub-tabs are rendered with standard conditional mounting (NO
 *    `forceMount` + `display:none`). Keeping Radix-portal-heavy panels
 *    (Operatori uses Dialog/Select/Tooltip) mounted while hidden was the
 *    root cause of the "Failed to execute 'removeChild' on 'Node'"
 *    crashes reported on Windows clients after restore.
 *  - Each sub-tab is wrapped in its own ErrorBoundary so a future similar
 *    crash stays isolated to that section instead of bricking the whole
 *    Impostazioni page.
 */
function ImpostazioniCombinate({ operators, setOperators, onRefresh, operatorRole, configTables, refreshConfigTables }) {
  const [activeSubTab, setActiveSubTab] = useState("dati-azienda");
  const operatoriAllowed = canManageOperators(operatorRole);
  // 30/06/2026 — Anche "Tabelle di configurazione" è gated come Operatori
  // (admin/responsabile only). Sub-tab nascosto per gli altri ruoli.
  const configTablesAllowed = canManageOperators(operatorRole);

  // Route guard: if a Studio/Agency operator somehow has the Operatori
  // sub-tab active (stale state, deep-link, manual injection), force them
  // back to a safe default. Cannot land on a section they cannot manage.
  useEffect(() => {
    if (activeSubTab === "operatori" && !operatoriAllowed) {
      setActiveSubTab("dati-azienda");
    }
    if (activeSubTab === "config-tables" && !configTablesAllowed) {
      setActiveSubTab("dati-azienda");
    }
  }, [activeSubTab, operatoriAllowed, configTablesAllowed]);

  return (
    <div className="space-y-6">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <TabsList
          className={`grid w-full mb-6 ${operatoriAllowed && configTablesAllowed ? "max-w-5xl grid-cols-6" : (operatoriAllowed || configTablesAllowed ? "max-w-4xl grid-cols-5" : "max-w-3xl grid-cols-4")}`}
        >
          <TabsTrigger
            value="dati-azienda"
            className="gis-heading data-[state=active]:bg-[var(--gis-green)] data-[state=active]:text-white"
            data-testid="settings-tab-dati-azienda"
          >
            <Building2 className="h-4 w-4 mr-2" />
            Dati Azienda
          </TabsTrigger>
          {/* Operatori sub-tab — visible only to amministratore/responsabile.
              See OPERATORI_ALLOWED_ROLES at the top of this file. */}
          {operatoriAllowed && (
            <TabsTrigger
              value="operatori"
              className="gis-heading data-[state=active]:bg-[var(--gis-green)] data-[state=active]:text-white"
              data-testid="settings-tab-operatori"
            >
              <Users className="h-4 w-4 mr-2" />
              Operatori
            </TabsTrigger>
          )}
          {/* Tabelle di configurazione (30/06/2026) — admin/resp only.
              v1.9.1 · 22/07/2026 (bugfix): il testo lungo traboccava dal
              trigger. Label accorciato a "Tabelle" (icona Table2 chiarisce
              il senso) + tooltip HTML title per chi vuole conferma. */}
          {configTablesAllowed && (
            <TabsTrigger
              value="config-tables"
              className="gis-heading data-[state=active]:bg-[var(--gis-green)] data-[state=active]:text-white text-sm"
              data-testid="settings-tab-config-tables"
              title="Tabelle di configurazione (Tipo Pratica · Ente Coinvolto)"
            >
              <Table2 className="h-4 w-4 mr-2 shrink-0" />
              <span className="truncate">Tabelle</span>
            </TabsTrigger>
          )}
          <TabsTrigger
            value="preferenze"
            className="gis-heading data-[state=active]:bg-[var(--gis-green)] data-[state=active]:text-white"
            data-testid="settings-tab-preferenze"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Preferenze
          </TabsTrigger>
          <TabsTrigger
            value="backup"
            className="gis-heading data-[state=active]:bg-[var(--gis-green)] data-[state=active]:text-white"
            data-testid="backup-tab"
          >
            <Database className="h-4 w-4 mr-2" />
            Backup & Restore
          </TabsTrigger>
          {/* Design System — vetrina interna del brand (21/07/2026 · v1.5.0).
              Il tab Impostazioni è già gated da isAdmin nell'App, quindi
              questo sub-tab risulta automaticamente admin-only. */}
          <TabsTrigger
            value="design-system"
            className="gis-heading data-[state=active]:bg-[var(--gis-green)] data-[state=active]:text-white"
            data-testid="settings-tab-design-system"
          >
            <Palette className="h-4 w-4 mr-2" />
            Design System
          </TabsTrigger>
        </TabsList>

        {/* Only the active sub-tab is mounted: avoids React 19 reconciliation
            crashes caused by Radix portals (Dialog/Select/Tooltip) staying
            mounted in hidden panels. Each sub-tab is also wrapped in its
            own ErrorBoundary for additional isolation. */}
        <TabsContent value="dati-azienda" className="space-y-6">
          {activeSubTab === "dati-azienda" && (
            <SubTabErrorBoundary label="Dati Azienda" testid="dati-azienda">
              <ImpostazioniStudio />
            </SubTabErrorBoundary>
          )}
        </TabsContent>

        <TabsContent value="operatori" className="space-y-6">
          {activeSubTab === "operatori" && (
            operatoriAllowed ? (
              <SubTabErrorBoundary label="Operatori" testid="operatori">
                <Operatori
                  operators={operators}
                  setOperators={setOperators}
                  onRefresh={onRefresh}
                />
              </SubTabErrorBoundary>
            ) : (
              <UnauthorizedOperatori />
            )
          )}
        </TabsContent>

        {/* Tabelle di configurazione (30/06/2026) */}
        <TabsContent value="config-tables" className="space-y-6">
          {activeSubTab === "config-tables" && configTablesAllowed && (
            <SubTabErrorBoundary label="Tabelle di configurazione" testid="config-tables">
              <ConfigTablesSettings
                configTables={configTables || { practice_types: [], ente_options: [] }}
                refreshConfigTables={refreshConfigTables || (() => {})}
              />
            </SubTabErrorBoundary>
          )}
        </TabsContent>

        <TabsContent value="backup" className="space-y-6">
          {activeSubTab === "backup" && (
            <SubTabErrorBoundary label="Backup & Restore" testid="backup">
              <BackupRestore />
            </SubTabErrorBoundary>
          )}
        </TabsContent>

        <TabsContent value="preferenze" className="space-y-6">
          {activeSubTab === "preferenze" && (
            <SubTabErrorBoundary label="Preferenze" testid="preferenze">
              <SessionPreferencesSettings />
            </SubTabErrorBoundary>
          )}
        </TabsContent>

        <TabsContent value="design-system" className="space-y-6">
          {activeSubTab === "design-system" && (
            <SubTabErrorBoundary label="Design System" testid="design-system">
              <DesignSystemPage />
            </SubTabErrorBoundary>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default React.memo(ImpostazioniCombinate);
